import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { appQueryKeys } from "@/lib/queryKeys";

export function useUnreadMessages() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: unreadCount = 0 } = useQuery({
    queryKey: appQueryKeys.unreadMessages(user?.id),
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from("coach_messages")
        .select("*", { count: "exact", head: true })
        .eq("coach_id", user.id)
        .eq("read", false)
        .neq("sender_id", user.id);

      if (error) {
        console.error("Error fetching unread count:", error);
        return 0;
      }

      return count || 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Subscribe to real-time updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("unread-messages-badge")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "coach_messages",
          filter: `coach_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: appQueryKeys.unreadMessages(user.id) });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  return unreadCount;
}
